import Client from "../client";
import ResourceCollection from "../resources/resourceCollection";
import Resource from "../resources/resource";
import {chunk, flatten} from "lodash";

export interface ListArgs {
    skip?: number;
    take?: number;
}

export interface AllArgs {
    ids?: string[];
}

export type ResourcesById<TResource> = { [id: string]: TResource; };

// Repositories provide a helpful abstraction around the Octopus Deploy API
class BasicRepository<TExistingResource extends Resource,
                    TNewResource, // Should never have a `Links` property, which we rely on in `save`
                    TListArgs extends ListArgs = ListArgs,
                    TAllArgs extends AllArgs = AllArgs,
                    TGetArgs = {},
                    TCreateArgs = {},
                    TModifyArgs = {}> {

    protected client: Client;
    private collectionLinkName: string;

    constructor(collectionLinkName: string, client: Client) {
        this.collectionLinkName = collectionLinkName;
        this.client = client;
    }
    create(resource: TNewResource, args?: TCreateArgs): Promise<TExistingResource> {
        return this.client.create(this.client.getRootDocument().Links[this.collectionLinkName], resource, args);
    }
    modify(resource: TExistingResource, args?: TModifyArgs): Promise<TExistingResource> {
        return this.client.update(resource.Links.Self, resource, args);
    }
    del(resource: TExistingResource) {
        return this.client.del(resource.Links.Self);
    }
    list(args?: TListArgs): Promise<ResourceCollection<TExistingResource>> {
        return this.client.get(this.client.getRootDocument().Links[this.collectionLinkName], args);
    }
    get(id: string, args?: TGetArgs): Promise<TExistingResource> {
        const allArgs = this.extend(args || {}, {id});
        return this.client.get(this.client.getRootDocument().Links[this.collectionLinkName], allArgs);
    }

    all(args?: TAllArgs): Promise<TExistingResource[]> {
        if (args !== undefined && args.ids instanceof Array && args.ids.length === 0) {
            return new Promise((res) => { res([]); });
        }

        // http.sys has a max query string of about 16k chars. Our typical max id length is 50 chars
        // so if we are doing requests by id and have more than 300, split into multiple requests
        const maxIds = 300;
        if (args !== undefined && args.ids instanceof Array && args.ids.length > maxIds) {
            return this.batchRequestsById(args, maxIds);
        }

        const allArgs = this.extend(args || {}, {id: "all"});
        return this.client.get(this.client.getRootDocument().Links[this.collectionLinkName], allArgs);
    }

    allById(args?: any): Promise<ResourcesById<TExistingResource>> {
        return this.all(args).then(result => result.reduce((acc: any, resource) => {
            acc[resource.Id] = resource;
            return acc;
        }, {}));
    }

    save(resource: TExistingResource | TNewResource): Promise<TExistingResource> {
        if (isExistingResource(resource)) {
            return this.modify(resource);
        } else {
            return this.create(resource);
        }

        function isExistingResource(r: TExistingResource | TNewResource): r is TExistingResource {
            return !!(r as TExistingResource).Links && !!(r as TExistingResource).Id;
        }
    }

    private extend(arg1: any, arg2: any) {
        return {...arg1, ...arg2};
    }

    private batchRequestsById(args: TAllArgs, batchSize: number): Promise<TExistingResource[]> {
        const idArrays = chunk(args.ids, batchSize);
        const promises: Array<Promise<TExistingResource[]>> = idArrays.map(ids => {
            const newArgs = {...(args as any), ids, id: "all"};
            return this.client.get(this.client.getRootDocument().Links[this.collectionLinkName], newArgs);
        });
        return Promise.all(promises).then(result => flatten(result));
    }
}

export default BasicRepository;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/client/repositories/basicRepository.ts